import React, { useCallback, useState } from 'react';
import { Dialog } from '@mui/material';
import { ClientForm } from './clientForm';
import { AppDialogTitle } from '../../layout/appDialogTitle';
import { editClient } from '../../services/api/clientServices';
import { Client } from '../../models/client';
import { BorderRadius } from '../../styles/constants';
import { AppConfirmationSuccessful } from '../app/confirmation/appConfirmationSuccessful';
import useAppSnackbar from '../../hooks/useAppSnackbar';

interface Props {
  isOpen: boolean;
  onCloseEditDialog(): void;
  client: Client | null;
  onRequestSuccessful(): void;
}

export function ClientDialogEdit({
  isOpen,
  onCloseEditDialog,
  client,
  onRequestSuccessful,
}: Props): JSX.Element {
  const { onErrorSnackbar } = useAppSnackbar();
  const [isOpenConfirmation, setIsOpenConfirmation] = useState<boolean>(false);
  const [isLoadingRequest, setIsLoadingRequest] = useState<boolean>(false);

  const handleEditClient = useCallback(
    (editClientInfo: Partial<Client>) => {
      setIsLoadingRequest(true);
      if (client)
        editClient(client.id, { id: client.id, ...editClientInfo })
          .then(() => {
            onCloseEditDialog();
            onRequestSuccessful();
            setIsOpenConfirmation(true);
          })
          .then(() => {
            setTimeout(() => {
              setIsOpenConfirmation(false);
            }, 1000);
          })
          .catch((error) => {
            return onErrorSnackbar(error as Error);
          })
          .finally(() => setIsLoadingRequest(false));
    },
    [onCloseEditDialog, client, onErrorSnackbar, onRequestSuccessful]
  );

  const handleClose = useCallback(() => {
    onCloseEditDialog();
    setIsOpenConfirmation(false);
  }, [onCloseEditDialog]);

  return (
    <>
      <Dialog open={isOpen} sx={{ borderRadius: BorderRadius.SMALL }}>
        <AppDialogTitle title="Edit client" handleClose={handleClose} />
        <ClientForm
          handleClose={handleClose}
          onSubmit={handleEditClient}
          clientEdit={client}
          isLoading={isLoadingRequest}
        />
      </Dialog>
      <AppConfirmationSuccessful
        isOpen={isOpenConfirmation}
        message="Record successfully updated"
        onClose={handleClose}
      />
    </>
  );
}
